import React from 'react';
import ReactDOM from 'react-dom';
import { useDataTable } from '@/components/common/DataTable';
import { plato_api_model_videos_path } from '@/modules/routes';
import { flattenDeep } from '@/modules/TCIUtils';
import CaptionStatus from './CaptionStatus';

interface VideosTableProps {
  modelId: number;
}

const EMPTY_MESSAGE = 'No videos in this reading yet. Use Add Video in the editor toolbar to insert one.';
const CELL_CLASS = 'tw-align-middle';

// AIDEV-NOTE: PLAT-17712 — read-only, and derived from the reading rather than a store of its own.
// ReferenceMaterial#sync_model_videos reconciles model_videos against the <video> elements in every
// body on save, so this reflects the LAST SAVE and not unsaved editor state. Adding Add/Delete here
// would recreate the section builder's bug, where a row can exist with nothing in the body pointing
// at it and the next save silently destroys it.
const VideosTable = ({ modelId }: VideosTableProps) => {
  const dataPath = plato_api_model_videos_path({
    for_model: { model_id: modelId, model_type: 'ReferenceMaterial' },
  });
  const { DataTable, resources } = useDataTable(dataPath);

  const columns = ['video_poster_url', 'video_title', 'video_video_file_name', 'captions'];

  // AIDEV-NOTE: PLAT-17712 — nothing in the stylesheets sets vertical-align for `table.dataTable td`,
  // so cells baseline-align against the 50px poster thumbnail and every other column drifts to the
  // bottom of the row. Every column opts into middle alignment rather than just the tall one.
  const columnDefs = [
    {
      className: CELL_CLASS,
      createdCell: (td, cellData, rowData) => {
        ReactDOM.render(
          rowData.video_poster_url && <img alt="" src={rowData.video_poster_url} width="50px" />,
          td,
        );
      },
      targets: 0,
      title: 'Poster',
      width: '100px',
    },
    {
      className: CELL_CLASS,
      targets: 1,
      title: 'Title',
    },
    {
      className: CELL_CLASS,
      createdCell: (td, cellData, rowData) => {
        ReactDOM.render(
          <a href={rowData.video_url} rel="noopener noreferrer" target="_blank">
            {rowData.video_video_file_name}
          </a>,
          td,
        );
      },
      targets: 2,
      title: 'File',
    },
    {
      // DataTables puts `className` on the header cell as well as the body cells, so the column
      // heading stays aligned with the statuses under it.
      className: `${CELL_CLASS} center`,
      createdCell: (td, cellData, rowData) => {
        ReactDOM.render(
          <CaptionStatus
            captionEnUrl={rowData.video_caption_en_url}
            captionEsUrl={rowData.video_caption_es_url}
            needsCaptions={rowData.video_needs_captions}
            needsCaptionsEs={rowData.video_needs_captions_es}
          />,
          td,
        );
      },
      sortable: false,
      targets: 3,
      title: 'Captions',
      width: '160px',
    },
  ];

  return (
    <DataTable
      autoWidth={false}
      columnDefs={columnDefs}
      columns={columns}
      info={false}
      noRecordsMessage={EMPTY_MESSAGE}
      paging={false}
      resources={resources.map(resource => ({ ...flattenDeep(resource), captions: '' }))}
    />
  );
};

export default VideosTable;
